import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useStripeMode } from "@/hooks/useStripeMode";
import usePageMeta from "@/hooks/usePageMeta";
import { UNLOCK_BENEFITS } from "@/lib/unlockBenefits";

export default function Pricing() {
  const { user } = useAuth();
  const stripeMode = useStripeMode();
  const cta = user && user !== false ? "/visualiser/new" : "/register";
  usePageMeta({
    title: "Pricing — ColourMatch AI Paint Visualiser (Free + $29.99)",
    description: "ColourMatch AI paint visualiser pricing for Australian homeowners: free first render, one-off AUD $29.99 per project — unlimited interior and exterior paint colours, up to 8 photos, HD downloads and a paint specification PDF.",
    path: "/pricing",
  });

  const free = ["Upload a photo", "1 AI render per project", "Watermarked preview", "Surface and colour selection"];
  const paid = UNLOCK_BENEFITS;

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12 lg:py-20" data-testid="pricing-page">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="text-center mb-12"
      >
        <span className="text-xs font-semibold tracking-widest uppercase text-brand-teal">Pricing</span>
        <h1 className="mt-3 text-4xl sm:text-6xl font-extrabold tracking-tight text-brand-ink">
          Simple <span className="text-brand-teal">Pricing.</span>
        </h1>
        <p className="mt-4 text-brand-muted max-w-lg mx-auto">
          Try ColourMatch free. Unlock your project with a one-time payment of $29.99 AUD. No subscription. Access your unlocked project anytime.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-5">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-3xl p-8 shadow-soft" data-testid="tier-free"
        >
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-muted mb-4">Free preview</span>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-5xl font-extrabold text-brand-ink">$0</span>
          </div>
          <p className="text-xs text-brand-muted mb-7">Always free, no card needed</p>
          <ul className="space-y-3 mb-8">
            {free.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-brand-ink">
                <span className="w-5 h-5 rounded-full bg-brand-sand flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={12} className="text-brand-ink" strokeWidth={3} />
                </span>
                {f}
              </li>
            ))}
          </ul>
          <Link to={cta} data-testid="tier-cta-free"
            className="w-full h-12 bg-brand-sand text-brand-ink rounded-2xl text-sm font-semibold hover:bg-brand-border transition-all tap-spring flex items-center justify-center">
            Start a design
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative bg-brand-ink text-white rounded-3xl p-8 overflow-hidden" data-testid="tier-basic"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-teal/40 blur-3xl pointer-events-none" />
          <div className="relative">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-teal mb-4">Basic unlock</span>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-extrabold tracking-tight">$29.99</span>
              <span className="text-sm text-white/60">AUD</span>
            </div>
            <p className="text-xs text-white/70 mb-7">One-time · per project · lifetime access</p>
            <ul className="space-y-3 mb-8">
              {paid.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <span className="w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <Link to={cta} data-testid="tier-cta-basic"
              className="w-full h-12 bg-brand-navy text-white rounded-2xl text-sm font-semibold hover:bg-brand-teal transition-all tap-spring flex items-center justify-center gap-2">
              Try a design first <ArrowRight size={16} />
            </Link>
          </div>
        </motion.div>
      </div>
      <p className="text-xs text-brand-muted text-center mt-10" data-testid="pricing-stripe-mode">
        Payments secured by Stripe
        {stripeMode.test && " · Test mode"}
      </p>
    </div>
  );
}
